import debounce from "./timed-debounce";
import THREE from "three";

import * as dte from "../DtEventTypes";
import * as et from "../../application/EventTypes";
import {Label3D} from "../../gui/Label3D";
import {PointMarker} from "../../gui/PointMarker";
import {get3DSelectionBounds} from "../../tools/FitToViewUtil";
import {DtConstants} from "../schema/DtConstants";
import {ElementFlags} from "../schema/dt-schema";
import {StreamUtils} from "./StreamUtils";
import {nowSec} from "./timeline/time";

const { StreamStates, StreamStatePriority } = DtConstants;

/**
 * The binning clustering will partition the screen space in regions of 2^{BINARY_SHIFT_FOR_BINNING} px
 *
 * 6 (64px) was chosen because it's the step bigger than the cluster icons with text ~45px and looks good visually.
 */
const BINARY_SHIFT_FOR_BINNING = 6;

const _tmpV3 = new THREE.Vector3();
const _tmpM4 = new THREE.Matrix4();

const toCanvasCoords = (position, projectionMatrix, matrixWorldInverse, containerBounds) => {
	const p = _tmpV3.copy(position);
	_tmpM4.multiplyMatrices(projectionMatrix, matrixWorldInverse);

	p.applyProjection(_tmpM4);

	if(p.x < -1 || p.x > 1 || p.y < -1 || p.y > 1 || p.z < -1 || p.z > 1) {
		return;
	}

	return new THREE.Vector2(
		Math.round(((p.x + 1) / 2) * containerBounds.width),
		Math.round(((-p.y + 1) / 2) * containerBounds.height)
	);
}

const CIRCLE_RADIUS = 8;
const CLUSTER_CIRCLE_RADIUS = 12;
const STREAM_LABEL_ID_PREFIX = "stream-label";
const MARKER_TYPES_ENABLED_BY_DEFAULT = new Set(Object.values(StreamStates));

function computeLblTextShift(clusterSize) {
	// Redoing old logic numerically
	if (clusterSize > 1000) return '0.6em';
	if (clusterSize > 100) return '1.2em';
	if (clusterSize > 10) return '1.8em';
	return '2.4em';
}

const getLabelIcon = (labelId, clusterSize = 1) => {
	let r = CIRCLE_RADIUS;
	let x = '';
	if (clusterSize > 1) {
		r = CLUSTER_CIRCLE_RADIUS;
		x = computeLblTextShift(clusterSize);
	}
	return `
		<g id=${labelId}>
			<circle r="${r}" cx="25" cy="27.5" fill="none" stroke-width="1.5" />
			<circle r="${r}" cx="25" cy="27.5" fill="currentColor" opacity="0.55" stroke-width="0" />
		</g>
		<g>
			<text x="${x}" y="1.1em">${clusterSize}</text>
		</g>`;
}

// modifies the SVG instead of recreating it.
function setIconComponent(iconEl, clusterSize) {
	let r = CIRCLE_RADIUS;
	let x = '';
	let l = '';
	if (clusterSize > 1) {
		r = CLUSTER_CIRCLE_RADIUS;
		x = computeLblTextShift(clusterSize);
		l = String(clusterSize);
	}

	const circleGroup = iconEl.children[0];
	circleGroup.children[0].setAttribute('r', r);
	circleGroup.children[1].setAttribute('r', r);

	const textEl = iconEl.children[1].children[0];
	textEl.textContent = l;
	textEl.setAttribute('x', x);
}


// Matches client theme colors
export const COLOR_SCHEME = {
	active: '#0696d7', // brand: COLORS.blue500,
	success: '#6a9728', // status-ok: COLOR.green600,
	warning: '#faa21b', // status-warning: COLORS.yellow500,
	offline: '#4d4d4d', // info: COLORS.charcoal80,
	nodata: '#4d4d4d', // same as offline here
	error: '#eb5555', // status-error: COLORS.red500,
}

const ACTIVE_COLOR = COLOR_SCHEME.active;

export class StreamMarkers {
	constructor(streamMgr, eventSource) {
		this.streamMgr = streamMgr;
		this.labels = {};
		this.matchHostVisibility = true;
		this.viewer = undefined;
		this.eventSource = eventSource;
		this.isActive = false;
		this.transitionDepth = 0;

		this.boundOnClick = this.onClick.bind(this);
		this.boundOnMouseOver = this.onMouseOver.bind(this);
		this.boundOnMouseOut = this.onMouseOut.bind(this);
		this.boundOnStreamsInfoChanged = this.onStreamsInfoChanged.bind(this);
		this.boundOnSelectionChanged = this.onSelectionChanged.bind(this);
		this.boundUpdate = this.update.bind(this);
		this.debouncedUpdate = debounce(() => this.#update(), 20);

		this.boundOnTransitionStarted = this.#onTransitionStarted.bind(this);
		this.boundOnTransitionEnded = this.#onTransitionEnded.bind(this);

		this.enabledMarkerTypes = new Set();
	}

	init(viewer) {
		this.viewer = viewer;

		this.eventSource.addEventListener(dte.DT_STREAMS_INFO_CHANGED_EVENT, this.boundOnStreamsInfoChanged);

		// Refresh markers when geometry loader goes idle, when camera moves, or when selections change
		this.viewer.addEventListener(et.AGGREGATE_SELECTION_CHANGED_EVENT, this.boundOnSelectionChanged);
		this.viewer.addEventListener(et.GEOMETRY_LOADED_EVENT, this.boundUpdate);
		this.viewer.addEventListener(et.CAMERA_CHANGE_EVENT, this.debouncedUpdate);

		this.viewer.addEventListener(et.TRANSITION_STARTED, this.boundOnTransitionStarted);
		this.viewer.addEventListener(et.TRANSITION_ENDED, this.boundOnTransitionEnded);

		this.streamMgr.view.addEventListener(dte.DT_TELEMETRY_TICK_EVENT, this.boundUpdate);
		this.streamMgr.view.addEventListener(dte.DT_TELEMETRY_CHANGED_EVENT, this.boundUpdate);
	}

	#isSelected(stream) {
		return this.streamMgr.defaultModel.selector?.isSelected(stream.dbId);
	}

	// Skip update when the markers are inactive and no streams are selected
	#skipUpdate() {
		if (!this.streamMgr.defaultModel) return true;

		const { myData, selector } = this.streamMgr.defaultModel;
		const selection = selector?.getSelection();
		const hasStreamSelection = selection?.some((dbId) => myData.dbId2flags[dbId] === ElementFlags.Stream);

		return !(this.isActive || hasStreamSelection);
	}

	// ensure markers are hidden while a transition animation is running
	#onTransitionStarted() {
		// transition started/ended do no appear strictly alternating but apparently can be stacked/nested
		// here we keep track of the stack depth and only care about the outmost transition
		if (this.transitionDepth++ === 0) {
			this.activeBeforeTransition = this.isActive;
		}
		this.hide();
	}

	// restore marker visibility after a transition has ended
	#onTransitionEnded() {
		// clear the stream's cached world position (will be recomputed by next update)
		const streams = this.streamMgr.getAllStreamInfosFromCache();
		if (streams) {
			for (let stream of streams) {
				stream.position = null;
			}
		}
		if (--this.transitionDepth === 0 && this.activeBeforeTransition) {
			this.show();
		}
	}

	show(markerTypes) {
		const markersUpdated = this.#setEnabledMarkerTypes(markerTypes);

		if (this.isActive) {
			if (markersUpdated) {
				this.update();
				this.eventSource.dispatchEvent({
					type: dte.DT_STREAM_MARKER_CHANGED_EVENT,
					change: { isVisible: true, markerTypes: this.enabledMarkerTypes, ctype: 'visibility' },
				});
			}
			return;
		}

		this.isActive = true;
		this.eventSource.dispatchEvent({
			type: dte.DT_STREAM_MARKER_CHANGED_EVENT,
			change: { isVisible: true, markerTypes: this.enabledMarkerTypes, ctype: 'visibility' },
		});

		// Load non-cached streams and refresh markers if there are streams
		this.streamMgr.getAllStreamInfos()
			.then((streams) => streams.length && this.onStreamsInfoChanged());
	}

	update() {
		if (this.#skipUpdate()) return;

		this.#doAnalysis()
			.then((analysis) => {
				this.analysis = analysis;
				this.debouncedUpdate();
			})
			.catch((err) => console.error(err));
	}

	// NOTE: called on every frame of camera move, so performance critical
	#update() {
		if (this.#skipUpdate()) return;

		// Gather possible visible streams and some of their properties.
		const visibleStreams = this.#gatherVisible();

		// Clustering of the stream markers
		const binnedClusters = this.#gatherBinnedClusters(visibleStreams);

		// Draw clusters
		this.#updateClusterDOMs(binnedClusters);
	}

	#gatherVisible() {
		const streams = this.streamMgr.getAllStreamInfosFromCache();

		// Sometimes the listener is not disposed of quick enough
		// this should catch those cases
		if (!streams?.length) {
			return [];
		}

		// Ensure the camera matrices and viewport remain the same across streams
		const {projectionMatrix, matrixWorldInverse} = this.viewer.getCamera();
		const containerBounds = this.viewer.navigation.getScreenViewport();

		const visibleStreams = [];

		for (let stream of streams) {

			if(!stream.hostElement) {
				continue;
			}

			const {model, hostId} = stream.hostElement;

			if(this.#isMarkerVisible(model, hostId, stream)) {
				// Avoid resetting stream position to stabilize clusters
				if (!stream.position) {
					const bounds = get3DSelectionBounds(this.viewer, [
						{model, selection: [hostId]},
					]);
					stream.position = bounds.isEmpty() ? undefined : bounds.getCenter();
				}

				if (stream.position) {
					stream.canvasPosition = toCanvasCoords(
						stream.position,
						projectionMatrix,
						matrixWorldInverse,
						containerBounds
					);
				}
			} else {
				stream.canvasPosition = null;
			}

			if(stream.canvasPosition) {
				visibleStreams.push(stream);
			} else {
				const cachedLabel = this.labels[stream.dbId];
				if (cachedLabel?.visible) {
					cachedLabel.setVisible(false);
				}
			}
		}

		return visibleStreams;
	}

	#gatherBinnedClusters(visibleStreams) {
		const screenBins = new Map();
		const highlightedBins = [];
		const getBin = (pos2D) => {
			let { x, y } = pos2D;

			// divided and truncated
			x = (x >> BINARY_SHIFT_FOR_BINNING) & 0xFFFF;
			y = (y >> BINARY_SHIFT_FOR_BINNING) & 0xFFFF;
			const packed = x | y << 16;

			let bin = screenBins.get(packed);
			if (bin) return bin;

			bin = {
				isSelected: false,
				status: StreamStates.NoData,
				streams: []
			};

			screenBins.set(packed, bin);
			return bin;
		}

		for (const stream of visibleStreams) {

			// Isolate the selected stream
			if (this.#isSelected(stream)) {
				highlightedBins.push({
					isSelected: true,
					status: this.analysis?.get(stream.dbId) || StreamStates.NoData,
					streams: [stream]
				});
				continue;
			}

			const pos2D = stream.canvasPosition;
			const bin = getBin(pos2D);

			// Keep first element (cluster representative) stable.
			const isMin = bin.streams.length && bin.streams[0].dbId > stream.dbId;
			if (isMin) bin.streams.unshift(stream);
			else bin.streams.push(stream);

			// Adjust status (if we don't have an analysis fallback to NoData status)
			const status = this.analysis?.get(stream.dbId) ?? StreamStates.NoData;
			if (StreamStatePriority[status] > StreamStatePriority[bin.status]) {
				bin.status = status;
			}
		}

		if (highlightedBins.length > 0) {
			return [...screenBins.values(), ...highlightedBins];
		} else {
			return screenBins.values();
		}
	}

	#updateClusterDOMs(binnedClusters) {
		for (const { streams, isSelected, status } of binnedClusters) {
			const color = isSelected ? ACTIVE_COLOR : status;

			// Draw main one.
			const stream = streams[0];
			let label = this.labels[stream.dbId];

			if (!label) {
				label = this.createLabel(streams, stream);
				label.removeLabelListeners();
				label.addListenersToIconComponent(label.id);
			} else {
				label.cluster = streams;
			}

			label.updateColor(color);
			label.updateCount(streams.length);
			label.setVisible(true);

			// Turn off the rest.
			for (let i = 1; i < streams.length; i++) {
				const label = this.labels[streams[i].dbId];
				if (label?.visible) {
					// non-first item in the cluster, hide it
					label.setVisible(false);
				}
			}
		}
	}

	async #doAnalysis() {
		const streams = await this.streamMgr.getAllStreamInfos();
		const data = await this.streamMgr.view.getInstantReadings();
		if (data == null) return;

		const commonNow = nowSec();
		const time = this.streamMgr.view.time.resolve().unix();
		return streams.reduce((map, stream) => {
			return map.set(stream.dbId, StreamUtils.getConnectionStateFromInstantData(stream, data, time, commonNow));
		}, new Map());
	}

	#isMarkerVisible(model, hostId, stream) {
		// a marker is visible if
		// - the stream element itself is "visible"
		// - its host element is visible (and matchHostVisibility is true)
		// - one of above condition is satisfied and marker group visible or stream is selected

		const isVisible = this.streamMgr.defaultModel.visibilityManager?.isNodeVisible(stream.dbId) ||
			(this.matchHostVisibility && model.visibilityManager?.isNodeVisible(hostId));

		if (!isVisible) return false;

		const isMarkerTypeEnabled = this.enabledMarkerTypes.has(this.analysis?.get(stream.dbId) || DtConstants.StreamStates.Normal);

		return (this.isActive && isMarkerTypeEnabled) || this.#isSelected(stream);
	}

	#setEnabledMarkerTypes(markerTypes) {
		const equalSets = (a, b) => {

			if (a.size !== b.size) {
				return false;
			}

			for (let v of a ) {
				if (!b.has(v)) {
					return false;
				}
			}

			return true;
		}

		if (markerTypes) {
			if (!equalSets(markerTypes, this.enabledMarkerTypes)) {
				this.enabledMarkerTypes = markerTypes;
				return true;
			}

			return false;
		}

		// Reset to default only if current enabled marker types are different from the default
		if (!equalSets(MARKER_TYPES_ENABLED_BY_DEFAULT, this.enabledMarkerTypes)) {
			this.enabledMarkerTypes = new Set(MARKER_TYPES_ENABLED_BY_DEFAULT);
			return true;
		}

		return false;
	}

	createLabel(cluster, mainStream) {
		const labelId = STREAM_LABEL_ID_PREFIX + '-' + mainStream.fullId;
		const label = new PointMarker(
			this.viewer,
			mainStream.position,
			null,
			getLabelIcon(labelId, cluster.length)
		);

		label.id = labelId;
		label.cluster = cluster;

		// Could be creating a label after activating a cluster,
		// in that case we need to color the stream as active
		label.updateColor = () => {
			label.setColor(this.#isSelected(mainStream) ? ACTIVE_COLOR : this.#getLabelColor(label));
		};

		label.updateCount = (count) => {
			// Only child of container since we use innerHTML and 2 child deep per `PointMarker<createIcon>`.
			const iconEl = label.container.children[0].children[0];
			setIconComponent(iconEl, count);
		};

		label.addEventListener(Label3D.Events.CLICK, this.boundOnClick);
		label.addEventListener(Label3D.Events.ON_MOUSE_OVER, this.boundOnMouseOver);
		label.addEventListener(Label3D.Events.ON_MOUSE_OUT, this.boundOnMouseOut);
		this.labels[mainStream.dbId] = label;

		return label;
	}

	#getLabelColor(label) {
		if (!label || !this.analysis) {
			return COLOR_SCHEME.nodata;
		}

		return this.#getClusterColor(label.cluster);
	}

	#getClusterColor(cluster) {
		const isOffline = cluster.find(stream => this.analysis?.get(stream.dbId) === StreamStates.Offline);
		if (isOffline) {
			return COLOR_SCHEME.offline;
		}
		const inAlert = cluster.find(stream => this.analysis?.get(stream.dbId) === StreamStates.Alert);
		if (inAlert) {
			return COLOR_SCHEME.error
		}
		const inWarning = cluster.find(stream => this.analysis?.get(stream.dbId) === StreamStates.Warning);
		if (inWarning) {
			return COLOR_SCHEME.warning;
		}
		const inSuccess = cluster.find(stream => this.analysis?.get(stream.dbId) === StreamStates.Normal);
		if (inSuccess) {
			return COLOR_SCHEME.success;
		}

		// No streams or all streams had "NoData" but didn't meet the definition of offline.
		return COLOR_SCHEME.nodata;
	}

	#removeCachedLabel(dbId) {
		const label = this.labels[dbId];
		label.removeEventListener(Label3D.Events.CLICK, this.boundOnClick);
		label.removeEventListener(Label3D.Events.ON_MOUSE_OVER, this.boundOnMouseOver);
		label.removeEventListener(Label3D.Events.ON_MOUSE_OUT, this.boundOnMouseOut);
		label.dispose();

		delete this.labels[dbId];
	}

	#removeAllCachedLabels() {
		for (const dbId in this.labels) {
			this.#removeCachedLabel(dbId);
		}

		this.labels = {}; // sanity wipe
	}

	hide() {
		if (!this.isActive) {
			return;
		}

		this.isActive = false;
		this.#setEnabledMarkerTypes(new Set());
		this.eventSource.dispatchEvent({
			type: dte.DT_STREAM_MARKER_CHANGED_EVENT,
			change: { isVisible: false, markerTypes: this.enabledMarkerTypes, ctype: 'visibility' },
		});

		this.debouncedUpdate.cancel();
		for (const dbId in this.labels) {
			const label = this.labels[dbId];
			label.setVisible(false);
		}

		this.#removeAllCachedLabels();
		this.update();
	}

	dispose() {
		this.eventSource.removeEventListener(dte.DT_STREAMS_INFO_CHANGED_EVENT, this.boundOnStreamsInfoChanged);
		this.viewer.removeEventListener(et.AGGREGATE_SELECTION_CHANGED_EVENT, this.boundOnSelectionChanged);
		this.viewer.removeEventListener(et.GEOMETRY_LOADED_EVENT, this.boundUpdate);
		this.viewer.removeEventListener(et.CAMERA_CHANGE_EVENT, this.debouncedUpdate);

		this.viewer.removeEventListener(et.TRANSITION_STARTED, this.boundOnTransitionStarted);
		this.viewer.removeEventListener(et.TRANSITION_ENDED, this.boundOnTransitionEnded);

		this.streamMgr.view.removeEventListener(dte.DT_TELEMETRY_TICK_EVENT, this.boundUpdate);
		this.streamMgr.view.removeEventListener(dte.DT_TELEMETRY_CHANGED_EVENT, this.boundUpdate);

		this.hide();
		this.#removeAllCachedLabels();
	}

	onClick({event, target: label}) {
		event.preventDefault();
		event.stopPropagation();

		const defaultModel = this.streamMgr.defaultModel;
		const dbIds = label.cluster.map(stream => stream.dbId);
		if (event.shiftKey) {
			this.viewer.toggleSelect(dbIds, defaultModel);
		} else {
			this.viewer.select(dbIds, defaultModel);
		}
	}

	onMouseOver({event, target: label}) {
		event.preventDefault();
		event.stopPropagation();
		label.setColor(ACTIVE_COLOR);
		this.viewer.dispatchEvent({
			type: dte.DT_STREAM_MARKER_MOUSE_OVER,
			target: label,
			cluster: label.cluster,
			event
		});
	}

	// noinspection JSUnusedGlobalSymbols
	onHighlightChange({stream, highlight}) {
		if (this.#isSelected(stream)) {
			// don't change highlight if marker is selected
			return;
		}

		let label = this.labels[stream.dbId];
		if (!label) {
			label = Object.values(this.labels).find(lbl => lbl.cluster?.find(s => s.dbId === stream.dbId));
		}
		label?.setColor(highlight ? ACTIVE_COLOR : this.#getLabelColor(label));
	}

	onMouseOut({event, target: label}) {
		event.preventDefault();
		event.stopPropagation();
		label.updateColor();
		// TODO: cluster property duplication can be deleted as soon client code will be adjusted
		this.viewer.dispatchEvent({type: dte.DT_STREAM_MARKER_MOUSE_OUT, target: label, cluster: label.cluster, event});
	}

	onSelectionChanged() {
		const defaultModel = this.streamMgr.defaultModel;

		if (!defaultModel)
			return;

		if (defaultModel.isLoadDone() && !this.streamMgr.facility.isModelVisible(defaultModel)) {
			this.hide();
			return;
		}

		// Clean up labels that are not selected anymore
		if (!this.isActive) {
			for (const dbId in this.labels) {
				if (!this.streamMgr.defaultModel.selector?.isSelected(dbId)) {
					this.#removeCachedLabel(dbId);
				}
			}
		}

		this.update();
	}

	onStreamsInfoChanged() {
		// Prevents clusters creation/update if markers are deactivated
		if (this.#skipUpdate()) return;

		// remove cached labels for streams that disappeared or have no host anymore
		const streamsWithHost = this.streamMgr.getAllStreamInfosFromCache().reduce((idSet, stream)=>{
			if(stream.hostElement) {
				idSet.add(stream.dbId);
			}
			return idSet;
		}, new Set());

		for(let dbId in this.labels) {
			// The host existence check requires the dbId to be converted to a numeric type
			if(!streamsWithHost.has(Number(dbId))) {
				this.#removeCachedLabel(dbId);
			}
		}
		this.update();
	}

	async setVisibility(isVisible, markerTypes) {
		if (isVisible) {
			this.show(markerTypes);
		} else {
			this.hide();
		}
	}

	getStateForView() {
		return this.enabledMarkerTypes;
	}
}
